import type { RenderContext, VideoFrame } from '../types';
import { BaseEffect, getColorGradeProgram } from './base';
import { canReadCanvasPixels, applyCssColorGrade } from '../canvasSafe';

const DEFAULTS = {
  contrast: 1,
  saturation: 1,
  temperature: 0,
  shadows: 0,
  highlights: 0,
  exposure: 0,
};

export class ColorGradeEffect extends BaseEffect {
  constructor(id: string, clip: Partial<import('../types').EffectTimelineClip>) {
    super(id, 'color_grade', clip, DEFAULTS);
  }

  protected renderGpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    const gl = context.gpu!.gl;
    const program = getColorGradeProgram(gl);
    return this.runShaderPass(frame, program, (_gl, prog) => {
      prog.setUniform1i('u_texture', 0);
      prog.setUniform1f('u_contrast', params.contrast);
      prog.setUniform1f('u_saturation', params.saturation);
      prog.setUniform1f('u_temperature', params.temperature);
      prog.setUniform1f('u_shadows', params.shadows);
      prog.setUniform1f('u_highlights', params.highlights);
      prog.setUniform1f('u_exposure', params.exposure);
    }, context);
  }

  protected renderCpu(frame: VideoFrame, params: Record<string, number>, _context: RenderContext): VideoFrame {
    const canvas = document.createElement('canvas');
    canvas.width = frame.width;
    canvas.height = frame.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return frame;
    const src = frame.canvas as CanvasImageSource;

    if (!canReadCanvasPixels(frame.canvas as HTMLCanvasElement)) {
      applyCssColorGrade(ctx, src, frame.width, frame.height, params);
      return { width: frame.width, height: frame.height, canvas, texture: null };
    }

    ctx.drawImage(src, 0, 0);
    const img = ctx.getImageData(0, 0, frame.width, frame.height);
    const d = img.data;
    const contrast = params.contrast;
    const sat = params.saturation;
    const temp = params.temperature;
    const shadows = params.shadows;
    const highlights = params.highlights;
    const exposure = Math.pow(2, params.exposure);

    for (let i = 0; i < d.length; i += 4) {
      let r = d[i] / 255 * exposure;
      let g = d[i + 1] / 255 * exposure;
      let b = d[i + 2] / 255 * exposure;
      r = (r - 0.5) * contrast + 0.5;
      g = (g - 0.5) * contrast + 0.5;
      b = (b - 0.5) * contrast + 0.5;
      const lum = 0.299 * r + 0.587 * g + 0.114 * b;
      r = lum + (r - lum) * sat;
      g = lum + (g - lum) * sat;
      b = lum + (b - lum) * sat;
      r += temp * 0.15;
      b -= temp * 0.15;
      if (lum < 0.45) {
        r += shadows * 0.25;
        g += shadows * 0.25;
        b += shadows * 0.25;
      }
      if (lum > 0.55) {
        r += highlights * 0.2;
        g += highlights * 0.2;
        b += highlights * 0.2;
      }
      d[i] = Math.min(255, Math.max(0, r * 255));
      d[i + 1] = Math.min(255, Math.max(0, g * 255));
      d[i + 2] = Math.min(255, Math.max(0, b * 255));
    }
    ctx.putImageData(img, 0, 0);
    return { width: frame.width, height: frame.height, canvas, texture: null };
  }
}
